import { useState, useEffect } from 'react';
import {
	Customer,
	Address,
} from '../components/tabs/incidentTab/CustomerAddressSelection';
import { createIncidentPatrolSchedule } from 'api/canguard/createIncidentPatrolSchedule';
import { useNotificationProvider } from 'hooks';
import { useScheduleContext } from '../context/ScheduleProvider';
import { GuardTabs } from 'pages/CANguard/CANguard';
import { useGuardRoleContext } from '../context/GuardRoleProvider';

interface IncidentTabStates {
	workTimeLoss: boolean;
	selectedPatrol: ScheduleAssignment | null;
	inputValidation: IncidentInputValidationErrors;
	selectedCustomer: Customer | null;
	selectedAddress: Address | null;
	selectedIncidentChecklist: any;
	startTime: Date | null;
	loading: boolean;
}
interface IncidentTabActions {
	handleWorkTimeLossChange: (value: boolean) => void;
	handleSelectedPatrolChange: (patrol: ScheduleAssignment | null) => void;
	handleStartTimeChange: (date: Date | null) => void;
	handleSubmit: () => void;
	handleSelectedIncidentChecklistChange: (checklist: any) => void;
	handleSelectedCustomerChange: (customer: Customer | null) => void;
	handleSelectedAddressChange: (address: Address | null) => void;
}

interface IncidentInputValidationErrors {
	incidentType?: string;
	customer?: string;
	address?: string;
	startTime?: string;
	patrol?: string;
}

const useIncidents = (goToTab: (tab: GuardTabs) => void) => {
	const [selectedIncidentChecklist, setSelectedIncidentChecklist] =
		useState(null);
	const [workTimeLoss, setWorkTimeLoss] = useState<boolean>(true);
	const [selectedPatrol, setSelectedPatrol] =
		useState<ScheduleAssignment | null>(null); // fields used when selected from selectedPatrol

	const [startTime, setStartTime] = useState<Date | null>(null);

	const [inputValidation, setInputValidation] =
		useState<IncidentInputValidationErrors>(
			{} as IncidentInputValidationErrors,
		);

	const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
		null,
	);
	const [selectedAddress, setSelectedAddress] = useState<Address | null>(
		null,
	);
	const [createdIncidentID, setCreatedIncidentID] = useState<number | null>(
		null,
	);

	const [loading, setLoading] = useState(false);

	const { isAdmin, guardSelectorOptions, realUserID } = useGuardRoleContext();
	const { schedule, goToPatrol, fetchSchedule } = useScheduleContext();
	const { notifyError, notifySuccess } = useNotificationProvider();

	const handleSelectedCustomerChange = (customer: Customer | null) => {
		setSelectedCustomer(customer);
		setSelectedAddress(null);
		clearValidationError('customer');
	};

	const handleSelectedAddressChange = (address: Address | null) => {
		setSelectedAddress(address);
		clearValidationError('address');
	};

	const handleSelectedPatrolChange = (patrol: ScheduleAssignment | null) => {
		setSelectedPatrol(patrol);
		clearValidationError('patrol');
	};

	const handleStartTimeChange = (date: Date | null) => {
		setStartTime(date);
		clearValidationError('startTime');
	};

	const handleSelectedIncidentChecklistChange = (checklist: any) => {
		setSelectedIncidentChecklist(checklist);
		clearValidationError('incidentType');
	};

	const handleWorkTimeLossChange = (value: boolean) => {
		setWorkTimeLoss(value);
		setInputValidation({});
	};

	const validateInput = () => {
		let _inputValidation: IncidentInputValidationErrors = {};

		if (!selectedIncidentChecklist) {
			_inputValidation.incidentType = 'Incident type cannot be empty';
		}

		if (workTimeLoss) {
			if (!selectedCustomer) {
				_inputValidation.customer = 'Customer cannot be empty';
			}
			if (!selectedAddress) {
				_inputValidation.address = 'Address cannot be empty';
			}
			if (!startTime) {
				_inputValidation.startTime = 'Start time cannot be empty';
			}
		} else {
			if (!selectedPatrol) {
				_inputValidation.patrol = 'Patrol cannot be empty';
			}
		}

		setInputValidation(_inputValidation);

		return Object.keys(_inputValidation).length === 0; // True if no errors, false otherwise.
	};

	const clearValidationError = (
		inputName: keyof IncidentInputValidationErrors,
	) => {
		if (inputValidation[inputName]) {
			setInputValidation((prevValidation) => {
				const updatedValidation = {
					...prevValidation,
				};
				delete updatedValidation[inputName];
				return updatedValidation;
			});
		}
	};

	const handleSubmit = async () => {
		if (!validateInput()) return;
		setLoading(true);

		try {
			const schedule: any = {
				// if selectedPatrol null, passing null for ScheduleAssignmentId will create new patrol. Otherwise attach to selectedPatrol.schedule_assignment_id
				ScheduleAssignmentId: selectedPatrol
					? selectedPatrol.schedule_assignment_id
					: null,
				Type: 'Incident',
				Checklists: [selectedIncidentChecklist],
				...(workTimeLoss && {
					//IF WORK TIME LOSS: WE NEED TO CREATE A NEW PATROL, SO WE NEED TO PASS IN THE CUSTOMER AND ADDRESS AND START TIME
					AddressId: selectedAddress?.Id,
					CustomerId: selectedCustomer?.Id,
					StartTime: startTime,
					EndTime: startTime, // Assuming EndTime is the same as StartTime for new incidents.
				}),
			};

			const date = selectedPatrol
				? selectedPatrol.work_date
				: startTime
				? new Date(new Date(startTime).setHours(0, 0, 0, 0))
				: null;

			const contactID = isAdmin
				? guardSelectorOptions.selectedGuardID
				: realUserID; // If admin, use selected guard ID, otherwise use realUserID

			if (!date || !contactID)
				throw new Error('Date or contactID is null');

			const createdIncidentID = await createIncidentPatrolSchedule(
				date,
				contactID,
				schedule,
			);
			fetchSchedule();
			setCreatedIncidentID(createdIncidentID); // fetchSchedule() will trigger the useEffect to navigate to the newly created incident.
		} catch (error) {
			notifyError('Failed to create incident.');
			console.error(
				'[useIncidents/handleSubmit] Error creating incident:',
				error,
			);
			return;
		} finally {
			setLoading(false);
		}
	};

	function resetSelectedStates() {
		setSelectedCustomer(null);
		setSelectedAddress(null);
		setSelectedPatrol(null);
		setSelectedIncidentChecklist(null);
		setStartTime(null);
		setWorkTimeLoss(true);
	}

	// Side effect to navigate to the newly created incident.
	// This effect will run when the schedule is updated.
	useEffect(() => {
		if (!createdIncidentID) return;

		const newPatrolIndex = schedule.findIndex(
			(patrol) => patrol.schedule_assignment_id === createdIncidentID,
		);

		if (newPatrolIndex >= 0) {
			goToPatrol(newPatrolIndex);
			goToTab(GuardTabs.Patrols);
			notifySuccess('Incident created successfully.');
			setCreatedIncidentID(null);
			resetSelectedStates();
		} else {
			console.error(
				"[useIncidents/useEffect] Could not find the newly created incident in the schedule.",
			);
		}
	}, [schedule]);

	const states: IncidentTabStates = {
		selectedPatrol,
		workTimeLoss,
		inputValidation,
		selectedCustomer,
		selectedAddress,
		selectedIncidentChecklist,
		startTime,
		loading,
	};
	const actions: IncidentTabActions = {
		handleWorkTimeLossChange,
		handleSelectedPatrolChange,
		handleSubmit,
		handleSelectedIncidentChecklistChange,
		handleStartTimeChange,
		handleSelectedCustomerChange,
		handleSelectedAddressChange,
	};
	return { states, actions };
};

export default useIncidents;
